/**
 * True when a vital value should be shown in read-only UI (includes numeric 0).
 */
export const isVitalValuePresentForDisplay = (value: unknown): boolean => {
  if (value == null || value === "") return false;
  if (typeof value === "number") return Number.isFinite(value);
  return true;
};

/**
 * Whether a temperature row should be shown in read-only UI and client exports.
 * `0` counts as present; method-only rows still show, so a recorded method is
 * never hidden just because the value is missing.
 * Use `hasTemperatureValue` to decide whether to write an Observation.
 */
export const hasTemperatureRowData = (
  temperatureValue: unknown,
  temperatureMethodValue: unknown,
): boolean => {
  if (isVitalValuePresentForDisplay(temperatureValue)) return true;
  if (temperatureMethodValue) return true;
  return false;
};

/**
 * Whether the vitals bots should write a temperature Observation.
 * Only the value counts: the unit always defaults to "F", and the method is optional.
 */
export const hasTemperatureValue = (temperatureValue: unknown): boolean =>
  isVitalValuePresentForDisplay(temperatureValue);

/**
 * Formats temperature display value for questionnaire responses
 * @param temperatureValue - The temperature value (number or string)
 * @param temperatureUnitValue - The temperature unit (F or C)
 * @param temperatureMethodValue - The temperature method (e.g., "Oral", "Rectal")
 * @returns Formatted temperature string
 */
export const formatTemperatureDisplay = (
  temperatureValue?: string | number | null | boolean,
  temperatureUnitValue?: string | number | null | boolean,
  temperatureMethodValue?: string | number | null | boolean,
): string => {
  let valueStr = "";
  if (typeof temperatureValue === "number") {
    if (Number.isFinite(temperatureValue)) valueStr = String(temperatureValue);
  } else if (temperatureValue != null && temperatureValue !== "") {
    valueStr = String(temperatureValue);
  }
  // Default to Fahrenheit if no unit is specified, as per business logic
  // The temperatureUnitValue should always be present when a temperature is recorded
  // since the default is "F" in the questionnaire forms
  const unit = String(temperatureUnitValue || "F");
  const method = String(temperatureMethodValue || "");

  if (!valueStr) return method;

  return `${valueStr}°${unit} ${method}`.trim();
};
